import { useState, useEffect, useCallback } from 'react';

/**
 * React Hook to extract and parse query parameters from the current URL,
 * updating when the query string changes.
 *
 * @template T
 * @returns {T} An object where keys are the query parameter names and values are their corresponding values. Defaults to `Record<string, string>`.
 *
 * @example
 * // URL: https://example.com?name=John&age=30&city=New%20York
 * // useQueryParams() returns: { name: "John", age: "30", city: "New York" }
 */
export default function useQueryParams<T extends Record<string, string> = Record<string, string>>(): T {
    const [queryParams, setQueryParams] = useState<T>(() => {
        if (typeof window === 'undefined') {
            return {} as T; // Return an empty object on the server
        }
        const urlSearchParams = new URLSearchParams(window.location.search);
        return Object.fromEntries(urlSearchParams.entries()) as T;
    });

    const updateQueryParams = useCallback(() => {
        if (typeof window !== 'undefined') {
            const urlSearchParams = new URLSearchParams(window.location.search);
            setQueryParams(Object.fromEntries(urlSearchParams.entries()) as T);
        }
    }, []);

    useEffect(() => {
        const handleHistoryChange = () => updateQueryParams();
        const handleHashChange = () => updateQueryParams();

        updateQueryParams();

        window.addEventListener('popstate', handleHistoryChange);
        window.addEventListener('pushstate', handleHistoryChange);
        window.addEventListener('replacestate', handleHistoryChange);
        window.addEventListener('hashchange', handleHashChange);

        return () => {
            window.removeEventListener('popstate', handleHistoryChange);
            window.removeEventListener('pushstate', handleHistoryChange);
            window.removeEventListener('replacestate', handleHistoryChange);
            window.removeEventListener('hashchange', handleHashChange);
        };
    }, [updateQueryParams]);

    return queryParams;
}
